import { useEffect, useMemo } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet-polylinedecorator';

// Temporary safety switch:
// some production bundles throw constructor errors from polyline decorator during zoom.
// Flow is still visible via animated lines/canvas layers in monitoring map.
const ENABLE_POLYLINE_DECORATOR = false;

const toSafePositions = (positions) => {
  if (!Array.isArray(positions)) return [];
  return positions
    .map((point) => {
      if (!Array.isArray(point) || point.length < 2) return null;
      const lat = Number(point[0]);
      const lon = Number(point[1]);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
      return [lat, lon];
    })
    .filter(Boolean);
};

const LeafletFlowDirection = ({
  positions,
  color = '#06b6d4',
  offset = '8%',
  repeat = '70px',
  pixelSize = 8,
  opacity = 0.95,
  className = '',
  smoothFactor = 1,
}) => {
  const map = useMap();
  const safePositions = useMemo(() => toSafePositions(positions), [positions]);

  useEffect(() => {
    if (!ENABLE_POLYLINE_DECORATOR) return undefined;
    if (!map || safePositions.length < 2 || typeof L.polylineDecorator !== 'function') return undefined;
    if (!L?.Symbol || typeof L.Symbol.arrowHead !== 'function') return undefined;

    let decorator = null;
    try {
      decorator = L.polylineDecorator(safePositions, {
        patterns: [
          {
            offset,
            repeat,
            symbol: L.Symbol.arrowHead({
              pixelSize,
              polygon: true,
              pathOptions: {
                color,
                fillColor: color,
                fillOpacity: opacity,
                opacity,
                weight: 1,
                className: `leaflet-flow-direction ${className}`.trim(),
              },
            }),
          },
        ],
      });
      decorator.addTo(map);
    } catch {
      decorator = null;
    }

    return () => {
      if (decorator) {
        try {
          map.removeLayer(decorator);
        } catch {
          // no-op
        }
      }
    };
  }, [className, color, map, offset, opacity, pixelSize, repeat, safePositions, smoothFactor]);

  return null;
};

export default LeafletFlowDirection;
